import { NonUserEmail, User, useRecipientsSelectRecipientsQuery } from 'schema'
import { SEPARATOR } from '../types'
import { useOptions } from '../../hooks/useOptions'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { useGlobalState } from 'context/GlobalStateContext'
import { UserSelectUser } from '../UserSelect/types'
import {
  UserSelectLabelUser,
  useUserSelectLabel,
} from '../UserSelect/hooks/useUserSelectLabel'
import {
  UserSelectTooltipUser,
  useUserSelectTooltip,
} from '../UserSelect/hooks/useUserSelectTooltip'
import {
  NonUserEmailRecipient,
  Recipient,
  RecipientsSelectContext,
} from './types'
import { useUserDeleted } from '../../../../hooks/useUserDeleted'
import { createStrictContext } from 'utils/createContext'
import { toLabeledValues } from '../../utils/labeledValues'
import { alphabeticalCompare } from 'shared'

const { Provider, useContextFn } = createStrictContext<RecipientsSelectContext>(
  'RecipientsSelectContext'
)

export const useRecipientsSelectContext = useContextFn

/**
 * a provider to give access to data and methods associated with the RecipientsSelect
 */
const RecipientsSelectProvider: React.FC = ({ children }) => {
  const {
    state: { projectGlobalId: globalId },
  } = useGlobalState()
  const { data, loading } = useRecipientsSelectRecipientsQuery({
    ...DefaultQueryHookOptions,
    skip: !globalId,
    variables: { globalId: globalId! },
  })

  const userLabel = useUserSelectLabel()
  const userTooltip = useUserSelectTooltip()
  const userDeleted = useUserDeleted()
  function toValue({
    __typename,
    id,
  }: {
    id: string | number
    __typename?: 'User' | 'NonUserEmail'
  }) {
    return `${__typename}${SEPARATOR}${id}`
  }
  function label(r: UserSelectLabelUser | NonUserEmailRecipient) {
    if (r.__typename === 'User') return userLabel(r as UserSelectUser)
    return r.label ?? r.address
  }
  function tooltip(r: UserSelectTooltipUser | NonUserEmailRecipient) {
    if (r.__typename === 'User') return userTooltip(r as UserSelectUser)
    if (!!r.label && !!r.description) return r.description
    else if (!!r.label) return r.address
    else return undefined
  }
  function deleted(r: Recipient) {
    if (r.__typename === 'NonUserEmail') return undefined
    return userDeleted(r as UserSelectUser)
  }

  const users = useOptions({
    items: data?.project?.users,
    value: toValue,
    label,
    tooltip,
    sort: (a, b) => alphabeticalCompare(label(a), label(b)),
  })
  const nonUserEmails = useOptions({
    items: data?.project?.nonUserEmails,
    value: toValue,
    label,
    tooltip,
    sort: (a, b) => alphabeticalCompare(label(a), label(b)),
  })

  return (
    <Provider
      value={{
        loading,
        options: [
          { label: 'Users', value: 1, options: users },
          { label: 'Non User Emails', value: 2, options: nonUserEmails },
        ],
        fromLabeledValues: (values) =>
          (values ?? []).reduce(
            (prev, { value }) => {
              const [type, id] = (value as string).split(SEPARATOR) as [
                User['__typename'] | NonUserEmail['__typename'],
                string
              ]
              if (type === 'User') prev.userIds.push(id)
              else if (type === 'NonUserEmail')
                prev.nonUserEmailIds.push(parseInt(id))
              return prev
            },
            { userIds: Array<string>(), nonUserEmailIds: Array<number>() }
          ),
        toLabeledValues: (recipients) =>
          recipients
            ? toLabeledValues(
                [...recipients.nonUserEmails, ...recipients.users],
                {
                  value: toValue,
                  label,
                  tooltip,
                  deleted,
                }
              )
            : [],
        toLabeledValue: (recipient) =>
          recipient
            ? toLabeledValues([recipient], {
                value: toValue,
                label,
                tooltip,
                deleted,
              })[0]
            : undefined,
        fromLabeledValue: (value) => {
          if (!value) return undefined
          const [type, id] = (value.value as string).split(SEPARATOR) as [
            User['__typename'] | NonUserEmail['__typename'],
            string
          ]
          if (type === 'User') return { userId: id }
          else if (type === 'NonUserEmail')
            return { nonUserEmailId: parseInt(id) }
          else return undefined
        },
      }}
    >
      {children}
    </Provider>
  )
}

export default RecipientsSelectProvider
